/* Built In Imports */
import { forwardRef, useEffect, useState } from 'react';

/* External Imports */
import {
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  Grid,
  Link,
  ListItem,
  Radio,
  RadioGroup,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  UnorderedList,
  useMultiStyleConfig,
  useTab,
} from '@chakra-ui/react';
import { render } from 'datocms-structured-text-to-html-string';
import parser from 'html-react-parser';
import { nanoid } from 'nanoid';

/* Internal Imports */
/* Components */
import SingleImage from '@components/Card/SingleImage';
import SectionTitle from '@components/Headings/SectionTitle';
import LazyLoadImageComponent from '@components/UI/LazyLoadImageComponent';
import SingleLineText from '@components/UI/SingleLineText';
import StructuredTextParser from '@components/Utility/ArticleParser/StructuredTextParser';
import { refineUrlDomain } from '@components/Utility/utils';

/* Services */
import { searchByFieldId } from '@components/Utility/Shared/SharedService';

/**
 *
 * @param {object} section
 * @returns ProgramDetails
 */
export const pageCss = {
  en: {
    tabTitle: {
      fontSize: {
        base: '16px',
        md: '22px',
      },
      color: 'red',
    },
    testimonialHeight: 571,
    cardTitle: {
      lineHeight: {
        base: '1.5rem',
        md: '1.6rem',
      },
    },
  },
};
let isViewGtmFired = false;

const ProgramDetailsTorontoSoon = ({
  section,
  region,
  lang,
  // pageData,
  // setCityId,
}) => {
  const sectionContent = section?.sectionContent;

  const [noUrl, setNoUrl] = useState('');
  const [yesUrl, setYesUrl] = useState('');
  const [isIeDone, setIsIeDone] = useState(true);
  const [selectedProg, setSelectedProg] = useState(null);

  const handleRadioClick = (event, data) => {
    event.preventDefault();
    if (data?.Status?.toLowerCase() !== 'full') {
      setYesUrl(data.Category);
      setNoUrl(data.Category);
      setSelectedProg(data);
    }
  };

  const emptySelection = () => {
    setYesUrl('');
    setNoUrl('');
    setSelectedProg(null);
  };

  useEffect(() => {
    if (typeof window !== undefined) {
      window.addEventListener('scroll', addViewGtm);
      return () => window.removeEventListener('scroll', addViewGtm);
    }
  }, []);

  const addViewGtm = () => {
    if (!isViewGtmFired) {
      const completedIE = document?.getElementById('have-you-completed');
      const rect = completedIE.getBoundingClientRect();
      const rectNum = Number(rect.bottom.toFixed());

      if (rectNum > 0 && rectNum < 180) {
        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push({
          event: 'view_item',
          ecommerce: {
            currency: '',
            value: '',
            items: [
              {
                item_id: '', // id of the program
                item_name: 'Ecstasy of Enlightenment', // Name of the program (Ex - EoE)
                affiliation: '',
                coupon: '',
                discount: '',
                index: '',
                item_brand: '', //IE Status <Yes/No>
                item_category: '', //IE Lang (User selected IE Language Ex- MR,EN,HI)
                item_category2: '',
                item_category3: '',
                item_category4: '',
                item_category5: '',
                item_list_id: '',
                item_list_name: '',
                item_variant: '',
                location_id: '',
                price: '',
                quantity: '',
              },
            ],
          },
        });
        isViewGtmFired = true;
        setTimeout(() => {
          isViewGtmFired = false;
        }, 4000);
      }
    }
  };

  const gtmClick = () => {
    if (typeof window !== 'undefined') {
      window.dataLayer = window.dataLayer || [];

      window.dataLayer.push({
        event: 'EoE_Register_Now',
        IE_status: isIeDone ? 'Yes' : 'No',
        IE_Lang: 'en',
        city: 'Toronto',
      });

      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: 'add_to_cart',
        ecommerce: {
          currency: '',
          value: '',
          items: [
            {
              item_id: selectedProg?.Category, // id of the program
              item_name: 'Ecstasy of Enlightenment', // Name of the program (Ex - EoE)
              affiliation: '',
              coupon: '',
              discount: '',
              index: '',
              item_brand: isIeDone ? 'Yes' : 'No', //IE Status <Yes/No>
              item_category: 'EN', //IE Lang (User selected IE Language Ex- MR,EN,HI)
              item_category2: '',
              item_category3: '',
              item_category4: '',
              item_category5: '',
              item_list_id: '',
              item_list_name: '',
              item_variant: '',
              location_id: 'Toronto',
              price: '',
              quantity: '',
            },
          ],
        },
      });
    }
  };

  const cardStyle = {
    Open: {
      boxShadow: '0 0 2px #979797',
      background: '#FFFFFF',
    },
    Selected: {
      boxShadow:
        '0px 0px 0px 2px #be3a1d,3.06087px 18.7518px 41px rgba(34, 30, 2, 0.08), 1.98389px 12.154px 24.0116px rgba(34, 30, 2, 0.0607407), 1.179px 7.22293px 13.0593px rgba(34, 30, 2, 0.0485926), 0.612173px 3.75037px 6.6625px rgba(34, 30, 2, 0.04), 0.249404px 1.52793px 3.34074px rgba(34, 30, 2, 0.0314074), 0.0566827px 0.347256px 1.61343px rgba(34, 30, 2, 0.0192593)',
      background: '#F5F0E5',
    },
    Full: {
      outline: 'none',
      boxShadow: 'none',
      background: '#F5F0E5',
    },
  };

  const CustomTab = forwardRef((props, ref) => {
    // 1. Reuse the `useTab` hook
    const tabProps = useTab({ ...props, ref });
    const isSelected = !!tabProps['aria-selected'];

    // 2. Hook into the Tabs `size`, `variant`, props
    const styles = useMultiStyleConfig('Tabs', tabProps);
    const Arrow = () => {
      return (
        <Box
          as="span"
          position="absolute"
          width="20px"
          display={isSelected ? 'block' : 'none'}
          height="11px"
          clipPath="polygon(0 0, 50% 100%, 100% 0)"
          left="50%"
          bottom="-10px"
          background="#FBAD2E"
          style={{ transition: 'width 1s' }}
          zIndex="0"
          transition="0.4s linear"
        ></Box>
      );
    };

    return (
      <Button
        __css={styles.tab}
        {...tabProps}
        width="50%"
        color={isSelected ? '#211637 !important' : '#7B7B7B  !important'}
        bg={isSelected ? '#FBAD2E' : '#E8DECB'}
        border="0"
        fontFamily="FedraSansStd-book"
        fontStyle="normal"
        fontWeight="600"
        fontSize={pageCss[lang]?.tabTitle?.fontSize}
        lineHeight="150.3%"
        _active={{ bg: '#FBAD2E', border: '0' }}
        _focus={{ bg: '#FBAD2E', border: '0' }}
        pos="relative"
        height={{ base: '45px', sm: '81px' }}
        p={{ base: '0', sm: '1rem' }}
      >
        {isSelected ? <Arrow /> : ''}

        {tabProps.children}
      </Button>
    );
  });

  return (
    <Box backgroundColor="#F1EBDF" pt={{ base: '30px', lg: '80px' }}>
      <SectionTitle titleObj={sectionContent[0]} mt="0" />
      <Box
        background=" url(https://static.sadhguru.org/d/46272/1694600647-26-3.png) right center no-repeat"
        mt="70px"
      >
        <Box
          display="flex"
          flexDir={{ base: 'column', md: 'row' }}
          maxW="850px"
          py="60px"
          pt="0"
          px={{ base: '15px', md: 0 }}
          w="full"
          mx="auto"
          gridGap={{ base: '25px', md: '55px' }}
          alignItems="center"
        >
          <Box zIndex={3}>
            <LazyLoadImageComponent
              src={sectionContent[1].image?.url}
              alt={sectionContent[1].image?.alt}
            />
          </Box>
          <Box
            width={{ base: 'full', md: '400px' }}
            pt={{ base: '0', md: '40px' }}
          >

            <Box>
              {searchByFieldId(sectionContent, 'ie-contact-us') && (
                <>
                  <StructuredTextParser
                    str={render(
                      searchByFieldId(sectionContent, 'ie-contact-us').body
                    )}
                    region={region}
                    lang={lang}
                    fontSize="20px"
                    color="#000000"
                    textAlign={{ base: 'center', md: 'left' }}
                    fontFamily="FedraSansStd-book"
                    mt="0px"
                    className="linkText"
                  />
                  <style>
                    {`
                      .linkText p a {
                        text-decoration: underline;
                      }
                    `}
                  </style>
                </>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      <Box w="100%" maxW="1170px" mx="auto" id="have-you-completed">
        <Box maxW="494px" m="20px auto">
          {searchByFieldId(sectionContent, 'completed-ie') && (
            <SingleLineText
              sectionContent={searchByFieldId(sectionContent, 'completed-ie')}
              fontSize={{ base: '24px', md: '30px' }}
              color="#02026C"
              lineHeight="normal"
              fontFamily="FedraSansStd-medium"
              paddingTop="0"
              textAlign="center"
            />
          )}
        </Box>
        <Tabs
          isFitted
          variant="enclosed"
          p="0"
          className="tabcontent"
          border="none"
          borderRadius="12px"
          paddingBottom="20px"
          id="prgTab"
          defaultIndex={0}
        >
          <TabList borderBottom="none">
            <CustomTab
              style={{ borderRadius: '10px 0 0 0' }}
              onClick={() => {
                emptySelection();
                setIsIeDone(true);
              }}
            >
              {sectionContent[4]?.titleText}
            </CustomTab>
            <CustomTab
              style={{ borderRadius: '0 10px 0 0' }}
              onClick={() => {
                emptySelection();
                setIsIeDone(false);
              }}
            >
              {sectionContent[7]?.titleText}
            </CustomTab>
          </TabList>
          <TabPanels m="0 auto" bg="#fff" mb="0">
            {/* Tab 1 starts here */}
            <TabPanel px={{ base: '15px', md: '0' }} py="20px" pb="30px">
              {searchByFieldId(sectionContent, 'yes-sel-lang-text') && (
                <>
                  <StructuredTextParser
                      str={render(
                        searchByFieldId(sectionContent, 'yes-sel-lang-text').body
                      )}
                      region={region}
                      lang={lang}
                      fontFamily="FedraSansStd-medium"
                      fontStyle="italic"
                      pb="0"
                      px="15px"
                      textAlign="center"
                      color="#cf4520"
                      py="5px"
                    />

                  {sectionContent[6]?._modelApiKey === 'simple_button' && (
                    <Box display="flex" placeContent="center" mt="40px">
                      <Link
                        href={refineUrlDomain(sectionContent[6]?.linkUrl,
                          region,
                          lang
                        )}
                        mb="20px"
                        display="block"
                        fontWeight={'500'}
                        fontFamily={'FedraSansStd-medium'}
                        fontSize={'18px'}
                        bgColor={'#cf4520'}
                        h={'auto'}
                        minH="50px"
                        textAlign="left"
                        padding={'14px 29px'}
                        borderRadius="5px"
                        color="#faf7f0"
                        textDecoration="none"
                        width={'auto'}
                        cursor={'pointer'}
                        _hover={{
                          bg: '#000054',
                        }}
                        target={'_blank'}
                        onClick={() => {
                          gtmClick();
                        }}
                      >
                        {selectedProg?.CTAButton ||
                          sectionContent[6]?.buttonText}
                      </Link>
                    </Box>
                  )}                  
                </>
              )}
            </TabPanel>

            {/* Tab 2 starts here */}
            <TabPanel px={{ base: '15px', md: '0' }} py="15px" pb="30px">
            {searchByFieldId(sectionContent, 'yes-sel-lang-text') && (
                <>
                  <StructuredTextParser
                      str={render(
                        searchByFieldId(sectionContent, 'yes-sel-lang-text').body
                      )}
                      region={region}
                      lang={lang}
                      fontFamily="FedraSansStd-medium"
                      fontStyle="italic"
                      pb="0"
                      px="15px"
                      textAlign="center"
                      color="#cf4520"
                      py="5px"
                    />
                  

                  {sectionContent[9]?._modelApiKey === 'simple_button' && (
                    <Box display="flex" placeContent="center" mt="40px">
                      <Link
                        href={refineUrlDomain(sectionContent[9]?.linkUrl,
                            region,
                            lang
                          )}
                        mb="20px"
                        display="block"
                        fontWeight={'500'}
                        fontFamily={'FedraSansStd-medium'}
                        fontSize={'18px'}
                        bgColor={'#cf4520'}
                        h={'auto'}
                        minH="50px"
                        textAlign="left"
                        padding={'14px 29px'}
                        borderRadius="5px"
                        color="#faf7f0"
                        textDecoration="none"
                        width={'auto'}
                        cursor={'pointer'}
                        _hover={{
                          bg: '#000054',
                        }}
                        target={'_blank'}
                        onClick={() => {
                          gtmClick();
                        }}
                      >
                        {selectedProg?.CTAButton ||
                          sectionContent[9]?.buttonText}
                      </Link>
                    </Box>
                  )}                  
                </>
              )}
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>
      <style jsx global>
        {`
          .textParagraph p {
            font-size: 18px;
            color: #be3a1d;
            text-transform: uppercase;
          }
          .textParagraph p:nth-child(2) {
            font-size: 16px;
            color: #28231e;
            text-transform: none;
            font-family: 'FedraSansStd-book';
          }
          @media only screen and (max-width: 360px) {
            .gridComponent {
              grid-template-columns: repeat(1, minmax(0, 1fr));
              margin-top: 10px;
            }
          }
        `}
      </style>
    </Box>
  );
};

export default ProgramDetailsTorontoSoon;
